import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { usePaywallFilter } from "@/hooks/use-paywall-filter";

import { fetchArticles } from "@/lib/queries";
import { TOPIC_LABEL, TOPIC_TAGLINE, TOPICS, type Topic } from "@/lib/topics";
import { useReadArticles } from "@/hooks/use-read-articles";
import { useTopicPreferences } from "@/hooks/use-topic-preferences";
import { ArticleListRow } from "@/components/ArticleListRow";
import { formatLabel } from "@/lib/format-detector";
import { renderWithWikiLinks } from "@/lib/wiki-link";
import { PaywallBadge } from "@/components/PaywallBadge";
import { formatArticleDate } from "@/lib/format-date";
import { normalizeTitle } from "@/lib/normalize-title";
import { detectFormat } from "@/lib/format-detector";
import { SeoNote } from "@/components/SeoNote";
import { PageTuneSlot } from "@/components/TunePanel";

const READING_FLOW = 15;

// ISO uge-nøgle for gruppering af arkivet
function isoWeekKey(dateStr: string): { key: string; year: number; week: number } {
  const d = new Date(dateStr);
  const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { key: `${tmp.getUTCFullYear()}-W${String(week).padStart(2, "0")}`, year: tmp.getUTCFullYear(), week };
}

function weekRangeLabel(items: { created_at: string }[]): string {
  const dates = items.map((i) => new Date(i.created_at)).sort((a, b) => a.getTime() - b.getTime());
  const first = dates[0];
  const last = dates[dates.length - 1];
  const fmt = (d: Date) => d.toLocaleDateString("da-DK", { day: "numeric", month: "long" });
  if (!first || !last) return "";
  if (first.toDateString() === last.toDateString()) return fmt(first);
  return `${fmt(first)} – ${fmt(last)}`;
}


export const Route = createFileRoute("/artikler")({
  loader: async ({ context }) => {
    const articles = await context.queryClient.ensureQueryData({
      queryKey: ["articles", { hidePaywall: true }],
      queryFn: () => fetchArticles({ hidePaywall: true }),
    });
    return { articles };
  },
  head: () => ({
    meta: [
      { title: "Artikler – Flâneriet" },
      { name: "description", content: "Samlingen af tekster: vin, kunst, jazz, poesi, mad, cykling, kultur og levekunst – kurateret dagligt." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flâneriet" },
      { property: "og:title", content: "Artikler – Flâneriet" },
      { property: "og:description", content: "Samlingen af tekster: vin, kunst, jazz, poesi, mad, cykling, kultur og levekunst." },
      { property: "og:url", content: "https://flaneriet.online/artikler" },
      { property: "og:image", content: "https://flaneriet.online/og/og-artikler.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Flâneriet – Artikler" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Artikler – Flâneriet" },
      { name: "twitter:description", content: "Samlingen af tekster: vin, kunst, jazz, poesi, mad, cykling, kultur og levekunst." },
      { name: "twitter:image", content: "https://flaneriet.online/og/og-artikler.png" },
    ],
    links: [{ rel: "canonical", href: "https://flaneriet.online/artikler" }],
  }),
  component: ArtiklerPage,
});



type TabKey = "alle" | "laeseklubben" | Topic;

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: "alle", label: "Alle" },
  ...TOPICS.map((t) => ({ key: t as TabKey, label: TOPIC_LABEL[t] })),
  { key: "laeseklubben", label: "Bøger" },
];

function isBookish(url: string, title: string) {
  const f = detectFormat(url, title);
  return f === "book" || f === "audiobook";
}

function sourceLabel(source: string | null | undefined, url: string) {
  if (source && !/^https?:\/\//i.test(source)) return source;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return source || "kilden";
  }
}

function ArtiklerPage() {
  const { hidePaywall } = usePaywallFilter();
  const [tab, setTab] = useState<TabKey>("alle");
  const [query, setQuery] = useState("");
  const { markRead, isRead } = useReadArticles();
  const { isEnabled, toggle, enableAll, disableAll, enabled } = useTopicPreferences();
  const [archiveOpen, setArchiveOpen] = useState(false);

  const TABS = useMemo(
    () => ALL_TABS.filter((t) => t.key === "alle" || t.key === "laeseklubben" || isEnabled(t.key as Topic)),
    [isEnabled]
  );

  // If the current tab was disabled, snap back to "alle"
  useEffect(() => {
    if (tab !== "alle" && tab !== "laeseklubben" && !isEnabled(tab as Topic)) {
      setTab("alle");
    }
  }, [tab, isEnabled]);

  useEffect(() => {
    setArchiveOpen(false);
  }, [tab, query, hidePaywall]);

  const articles = useQuery({
    queryKey: ["articles", { hidePaywall }],
    queryFn: () => fetchArticles({ hidePaywall }),
  });

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    let list = articles.data ?? [];
    if (tab === "laeseklubben") {
      list = list.filter((a: any) => isBookish(a.original_url, a.title));
    } else {
      list = list.filter((a: any) => !isBookish(a.original_url, a.title));
    }
    if (tab === "alle") {
      list = list.filter((a: any) => enabled.has(a.topic as Topic));
    }
    if (!q && (tab === "alle" || tab === "laeseklubben")) return list;
    if (!q) return list.filter((a) => a.topic === tab);
    if (tab !== "alle" && tab !== "laeseklubben") list = list.filter((a) => a.topic === tab);
    return list.filter((a: any) => {
      const hay = [a.title, a.source, a.summary, a.excerpt, TOPIC_LABEL[a.topic as Topic]]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [articles.data, tab, q, enabled]);

  const latestUpdated = filtered[0]?.created_at ?? null;

  return (
    <div>
      <PageTuneSlot>
        <section>
          <p className="eyebrow mb-3">Søg</p>
          <div className="relative">
            <Search size={16} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
            <label className="sr-only" htmlFor="tune-articles-search">Søg i artikler</label>
            <input
              id="tune-articles-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Hvad leder du efter?"
              className="w-full bg-transparent border-0 border-b border-rule outline-none focus:ring-0 focus:border-bordeaux py-2 pl-7 pr-2 font-sans text-[14px] text-ink placeholder:text-ink-soft transition-colors"
            />
          </div>
        </section>

        <section>
          <p className="eyebrow mb-3">Sektion</p>
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  aria-pressed={active}
                  className={`eyebrow px-3 py-1.5 rounded-full border transition-colors ${
                    active
                      ? "border-bordeaux text-bordeaux bg-bordeaux/5"
                      : "border-rule text-ink-soft hover:text-bordeaux hover:border-bordeaux"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
            <p className="eyebrow">Interesseområder</p>
            <div className="flex items-center gap-3">
              <button onClick={enableAll} className="meta italic hover:text-bordeaux transition-colors">Vælg alle</button>
              <button onClick={disableAll} className="meta italic hover:text-bordeaux transition-colors">Fravælg alle</button>
            </div>
          </div>
          <p className="meta italic mb-3">Slå fra det, du ikke vil se. Gemmes lokalt.</p>
          <div className="flex flex-wrap gap-2">
            {TOPICS.map((t) => {
              const on = isEnabled(t);
              return (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  aria-pressed={on}
                  className={`eyebrow px-3 py-1.5 rounded-full border transition-colors ${
                    on
                      ? "border-bordeaux text-bordeaux bg-bordeaux/5"
                      : "border-rule text-ink-soft hover:text-bordeaux hover:border-bordeaux"
                  }`}
                >
                  {TOPIC_LABEL[t]}
                </button>
              );
            })}
          </div>
        </section>
      </PageTuneSlot>


      <header className="pt-12 pb-6 border-b-[3px] border-b-double border-b-ink">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2.5">Samlingen · udvalgte fund</p>
            <h1 className="font-serif font-normal text-[2.4rem] md:text-[3rem] leading-[1.02] text-ink m-0">
              {tab === "alle" ? "Artikler" : tab === "laeseklubben" ? "Bøger" : TOPIC_LABEL[tab as Topic]}
            </h1>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-between gap-3 py-6 flex-wrap">
        <p className="meta italic">
          {q ? (
            <>Søgning efter «{query}» · {filtered.length} {filtered.length === 1 ? "resultat" : "resultater"}</>
          ) : tab === "alle" ? (
            "Alle sektioner, nyeste først."
          ) : tab === "laeseklubben" ? (
            "Bøger og lydbøger – til den lange læsning."
          ) : (
            TOPIC_TAGLINE[tab as Topic]
          )}
          {!q && latestUpdated && <> · Opdateret {formatArticleDate(latestUpdated, null)}</>}
        </p>

      </div>

      <section className="min-w-0">
        {filtered.length > 0 ? (
          <div>
            {filtered.slice(0, READING_FLOW).map((a: any, i: number) => {
              const fmt = detectFormat(a.original_url, a.title);
              const kicker = (
                <span className="inline-flex items-center gap-2">
                  {TOPIC_LABEL[a.topic as Topic]}
                  {fmt !== "article" ? <span className="text-accent-dark not-italic"> · {formatLabel(fmt)}</span> : null}
                  <PaywallBadge paywall={a.paywall} />
                </span>
              );
              const source = sourceLabel(a.source, a.original_url);
              const showLead = !q && i === 0;
              return (
                <ArticleListRow
                  key={a.id}
                  id={a.id}
                  kicker={kicker}
                  title={a.title}
                  href={a.original_url}
                  imageUrl={a.image_url ?? null}
                  topic={a.topic}

                  reason={a.selection_reason ? renderWithWikiLinks(a.selection_reason) : (a.summary ?? undefined)}
                  sourceMeta={source}
                  outLabel={`Læs hos ${source}`}
                  onOpen={() => markRead(a.id)}
                  variant={showLead ? "lead" : "row"}
                  unread={!isRead(a.id)}
                />
              );
            })}
          </div>
        ) : (
          <p className="meta italic py-16 text-center">Endnu ingen tekster her. Kom tilbage om lidt.</p>
        )}
      </section>

      {filtered.length > READING_FLOW && (() => {
        const older = filtered.slice(READING_FLOW);
        const byWeek = new Map<string, { year: number; week: number; items: any[] }>();
        for (const a of older) {
          const { key, year, week } = isoWeekKey(a.created_at);
          const bucket = byWeek.get(key) ?? { year, week, items: [] };
          bucket.items.push(a);
          byWeek.set(key, bucket);
        }
        const groups = Array.from(byWeek.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([key, group]) => ({ key, ...group }));

        return (
          <section className="pt-14 mt-10 border-t-[3px] border-t-double border-t-ink">
            <header className="flex items-baseline justify-between gap-4 flex-wrap pb-6 mb-2">
              <div>
                <p className="eyebrow mb-2.5">Bladr bagud</p>
                <h2 className="font-serif font-normal text-[1.9rem] md:text-[2.4rem] leading-[1.05] text-ink m-0">
                  Uge for uge
                </h2>
              </div>
              <p className="meta italic max-w-[26em]">
                Alt det ældre, samlet uge for uge – for den der vil grave.
              </p>
            </header>

            {!archiveOpen ? (
              <div className="text-center py-10">
                <button
                  onClick={() => setArchiveOpen(true)}
                  className="eyebrow text-bordeaux border-b border-bordeaux pb-0.5 hover:opacity-70 transition-opacity"
                >
                  Åbn arkivet ({older.length} tekster · {groups.length} {groups.length === 1 ? "uge" : "uger"})
                </button>
              </div>
            ) : (
              <div>
                {groups.map((g) => (
                  <section key={g.key} className="py-8 border-b border-rule last:border-b-0">
                    <div className="flex items-baseline justify-between border-b-2 border-ink pb-3 mb-4 gap-3 flex-wrap">
                      <h3 className="font-serif text-[22px] md:text-[26px] leading-none m-0">
                        Uge {g.week} · {g.year}
                      </h3>
                      <span className="font-mono text-[10.5px] tracking-[0.12em] uppercase text-muted">
                        {weekRangeLabel(g.items)} · {g.items.length} fund
                      </span>
                    </div>
                    <ul className="divide-y divide-rule">
                      {g.items.map((a: any) => {
                        const source = sourceLabel(a.source, a.original_url);
                        return (
                          <li key={a.id} className="py-3 grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-2 md:gap-6 items-baseline">
                            <span className="eyebrow flex items-center gap-2">
                              {TOPIC_LABEL[a.topic as Topic] ?? "Fund"}
                              <PaywallBadge paywall={a.paywall} />
                            </span>
                            <a
                              href={a.original_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => markRead(a.id)}
                              className="font-serif text-[17px] md:text-[19px] leading-[1.25] text-ink hover:text-bordeaux transition-colors min-w-0 [overflow-wrap:anywhere]"
                            >
                              {normalizeTitle(a.title)}
                            </a>

                            <span className="font-mono text-[10.5px] tracking-[0.1em] uppercase text-muted md:text-right">
                              {source}
                              {a.created_at ? ` · ${formatArticleDate(a.created_at)}` : ""}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
                <div className="text-center pt-8">
                  <button
                    onClick={() => setArchiveOpen(false)}
                    className="eyebrow text-ink-soft border-b border-rule pb-0.5 hover:text-bordeaux hover:border-bordeaux transition-colors"
                  >
                    Luk arkivet
                  </button>
                </div>
              </div>
            )}
          </section>
        );
      })()}


      <SeoNote eyebrow="Om samlingen">
        <p>
          Artikler er Flâneriets levende læsesal – dagligt kurateres tekster inden for naturvin,
          jazz og klassisk musik, samtidskunst, poesi og litteratur, god mad, cykling som livsstil,
          samt stoicisme og levekunst. Alt fører videre til den oprindelige kilde. Ingen uendelig
          scroll. Kun det, der er værd at læse ordentligt.
        </p>
        <p>
          Brug fanebladene til at bladre efter emne, eller åbn <em>Tilpas</em> for at vælge dine
          egne interesser – så tilpasser samlingen sig dig.
        </p>
      </SeoNote>
    </div>
  );
}
